import { apiClient } from './client';
import type { IrrigationLog, IrrigationKpi, IrrigationMethod } from '../types/api.types';

/** Resultado del descuento automático de insumos de bodega (opt-in `warehouse.autoDeductOnActivity`). */
export type IrrigationStockDeductStatus =
  | 'DEDUCTED' | 'SKIPPED_DISABLED' | 'NO_INPUTS' | 'NOT_CONFIGURED' | 'PARTIAL' | 'FAILED';

/** Respuesta de crear un riego: el registro más el estado del descuento de inventario. */
export type CreatedIrrigationLog = IrrigationLog & { stockDeduct?: IrrigationStockDeductStatus };

export interface CreateIrrigationInput {
  /** Zona o subzona regada (opcional) */
  moduleId?:      string;
  batchId:        string;
  method:         IrrigationMethod;
  meterReadings?: { start: number; end: number; unit: 'm3' | 'L' };
  durationMin?:   number;
  flowRateLph?:   number;
  description?:   string;
  /** Lámina en mm, solo si el usuario la conoce (el servidor la calcula si hay datos). */
  depthMm?:       number;
  notes?:         string;
}

/** Recent irrigation logs for a lote */
export const fetchIrrigationLogs = async (
  loteId: string,
  limit = 20,
  moduleId?: string,
): Promise<IrrigationLog[]> =>
  (await apiClient.get(`/api/lotes/${loteId}/irrigation`, { params: { limit, ...(moduleId ? { moduleId } : {}) } })).data;

/** Aggregated KPIs for a lote (L/kg, total volume, sessions) */
export const fetchIrrigationKpi = async (loteId: string, moduleId?: string): Promise<IrrigationKpi> =>
  (await apiClient.get(`/api/lotes/${loteId}/irrigation/kpi`, { params: moduleId ? { moduleId } : undefined })).data;

/** All recent irrigation logs across lotes (dashboard summary) — no page consumes this yet,
 * see selector-rancho Fase 2 Task 7 report: no cross-lote view to wire on IrrigationPage today. */
export const fetchRecentIrrigationLogs = async (limit = 50, ranchoId?: string | null): Promise<IrrigationLog[]> =>
  (await apiClient.get('/api/irrigation/recent', { params: { limit, ...(ranchoId ? { ranchoId } : {}) } })).data;

const METHOD_TO_BACKEND: Record<IrrigationMethod, 'MANUAL' | 'MEDIDOR' | 'SIMPLIFICADO'> = {
  MANUAL: 'MANUAL',
  METER: 'MEDIDOR',
  SIMPLIFIED: 'SIMPLIFICADO',
};

/** Register a manual irrigation event from the dashboard */
export const createIrrigationLog = async (loteId: string, input: CreateIrrigationInput): Promise<CreatedIrrigationLog & { budgetWarning?: string }> =>
  (await apiClient.post(`/api/lotes/${loteId}/irrigation`, {
    ...input,
    method: METHOD_TO_BACKEND[input.method],
  })).data;
